import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import toast from 'react-hot-toast';
import { PromptContextType, Prompt } from '../types';
import { promptsApi, handleApiError, isAuthError, isUserLoggedIn } from '../services/api';

const PromptContext = createContext<PromptContextType | undefined>(undefined);

export const usePrompts = (): PromptContextType => {
  const context = useContext(PromptContext);
  if (!context) {
    throw new Error('usePrompts must be used within a PromptProvider');
  }
  return context;
};

interface PromptProviderProps {
  children: ReactNode;
}

export const PromptProvider: React.FC<PromptProviderProps> = ({ children }) => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [filteredPrompts, setFilteredPrompts] = useState<Prompt[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const loadPrompts = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response = await promptsApi.getAll();
      // 确保获取正确的数据类型 - 如果response有data属性则使用，否则假设response本身就是数组
      const data: Prompt[] = Array.isArray(response) 
        ? response 
        : (response.data && Array.isArray(response.data) ? response.data : []);
      setPrompts(data);
      setFilteredPrompts(data);
      console.log('📝 获取到提示词:', data);
    } catch (error) {
      const message = handleApiError(error);
      // 认证相关错误且用户未登录时不显示toast（用户退出登录时会触发）
      if (!(isAuthError(message) && !isUserLoggedIn())) {
      toast.error(`加载提示词失败: ${message}`);
      }
      setPrompts([]);
      setFilteredPrompts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const addPrompt = async (prompt: Omit<Prompt, 'id' | 'createdAt' | 'updatedAt'>): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await promptsApi.create(prompt);
      toast.success('提示词添加成功！');
      
      // 更新本地状态而不是重新加载
      const newPrompt = response.data;
      if (newPrompt) {
        setPrompts(prev => [...prev, newPrompt]);
        setFilteredPrompts(prev => [...prev, newPrompt]);
      } else {
        // 如果没有返回数据，则重新加载
        await loadPrompts();
      }
    } catch (error) {
      const message = handleApiError(error);
      toast.error(`添加提示词失败: ${message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updatePrompt = async (id: number, promptData: Partial<Prompt>): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await promptsApi.update(id, promptData);
      toast.success('提示词更新成功！');
      
      // 更新本地状态
      const updatedPrompt = response.data;
      if (updatedPrompt) {
        setPrompts(prev => prev.map(p => p.id === id ? updatedPrompt : p));
        setFilteredPrompts(prev => prev.map(p => p.id === id ? updatedPrompt : p));
      } else {
        await loadPrompts();
      }
    } catch (error) {
      const message = handleApiError(error);
      toast.error(`更新提示词失败: ${message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const deletePrompt = async (id: number): Promise<void> => {
    try {
      setIsLoading(true);
      await promptsApi.delete(id);
      toast.success('提示词删除成功！');
      
      // 从本地状态中移除
      setPrompts(prev => prev.filter(p => p.id !== id));
      setFilteredPrompts(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      const message = handleApiError(error);
      toast.error(`删除提示词失败: ${message}`);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const togglePublicPrompt = async (id: number): Promise<void> => {
    try {
      console.log('开始切换提示词公开状态:', { id });
      
      const response = await promptsApi.togglePublic(id);
      console.log('切换提示词公开状态响应:', response);
      
      if (response.success) {
        // 使用API返回的实际状态，而不是前端推测的状态
        const actualIsPublic = response.data?.isPublic;
        const actualSharedAt = response.data?.sharedAt;
        
        // 更新本地状态中的isPublic和sharedAt字段
        setPrompts(prev => prev.map(p => 
          p.id === id ? { 
            ...p, 
            isPublic: actualIsPublic !== undefined ? actualIsPublic : !p.isPublic,
            sharedAt: actualSharedAt !== undefined ? actualSharedAt : (actualIsPublic ? new Date().toISOString() : null)
          } : p
        ));
        setFilteredPrompts(prev => prev.map(p => 
          p.id === id ? { 
            ...p, 
            isPublic: actualIsPublic !== undefined ? actualIsPublic : !p.isPublic,
            sharedAt: actualSharedAt !== undefined ? actualSharedAt : (actualIsPublic ? new Date().toISOString() : null)
          } : p
        ));
        
        // 检查API响应中的clearPublicCache标记
        if (response.clearPublicCache) {
          // 清除公共提示词的缓存，确保提示词广场立即更新
          const { apiPerformance } = await import('../services/api');
          apiPerformance.clearCache();
          console.log('🧹 已清除API缓存，确保提示词广场同步更新');
          
          // 发出强制刷新事件，确保所有类别都刷新
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('prompt-status-changed', {
              detail: { promptId: id, isPublic: actualIsPublic }
            }));
          }
        }
        
        const isPublic = actualIsPublic !== undefined ? actualIsPublic : !prompts.find(p => p.id === id)?.isPublic;
        toast.success(isPublic ? '提示词已分享到广场！' : '提示词已从广场移除');
      } else {
        console.error('服务器返回失败响应:', response);
        throw new Error(response.message || '操作失败');
      }
    } catch (error) {
      console.error('切换提示词公开状态失败:', {
        error,
        id,
        message: error instanceof Error ? error.message : '未知错误'
      });
      
      const message = handleApiError(error);
      
      // 根据错误类型提供更详细的错误信息
      if (message.includes('认证') || message.includes('401')) {
        toast.error('认证失败，请重新登录');
      } else if (message.includes('403')) {
        toast.error('您没有权限操作此提示词');
      } else if (message.includes('404')) {
        toast.error('提示词不存在或已被删除');
      } else if (message.includes('503') || message.includes('数据库')) {
        toast.error('服务器连接失败，请稍后重试');
      } else if (message.includes('500')) {
        toast.error('服务器内部错误，请稍后重试或联系技术支持');
      } else {
        toast.error(`操作失败: ${message}`);
      }
      
      throw error;
    }
  };

  const searchPrompts = useCallback((query: string): void => {
    setSearchQuery(query);
    const lowercaseQuery = query.toLowerCase().trim();
    
    if (!lowercaseQuery) {
      setFilteredPrompts(prompts);
    } else {
      const filtered = prompts.filter(prompt =>
        prompt.title.toLowerCase().includes(lowercaseQuery) ||
        prompt.content.toLowerCase().includes(lowercaseQuery) ||
        prompt.category.toLowerCase().includes(lowercaseQuery) ||
        prompt.tags.some(tag => tag.toLowerCase().includes(lowercaseQuery))
      );
      setFilteredPrompts(filtered);
    }
  }, [prompts]);

  // 当searchQuery变化时，重新应用搜索
  useEffect(() => {
    searchPrompts(searchQuery);
  }, [searchQuery, searchPrompts]);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  const value: PromptContextType = {
    prompts,
    filteredPrompts,
    searchQuery,
    isLoading,
    loadPrompts,
    addPrompt,
    updatePrompt,
    deletePrompt,
    togglePublicPrompt,
    searchPrompts,
  };

  return (
    <PromptContext.Provider value={value}>
      {children}
    </PromptContext.Provider>
  );
}; 